import { useEffect, useState } from "react";
import { CalendarDays } from "lucide-react";

import type { SchoolTerm } from "@/lib/schoolYear";

type Locale = "sv" | "en" | "es";

const T = {
  sv: {
    upcoming: "Kommande datum",
    empty: "Inga fler datum är publicerade för det här läsåret.",
    today: "I dag",
    tomorrow: "I morgon",
    inDays: (n: number) => `om ${n} ${n === 1 ? "dag" : "dagar"}`,
    showPast: "Visa avslutad termin",
    hidePast: "Dölj avslutad termin",
    done: "Avslutad",
  },
  en: {
    upcoming: "Coming up next",
    empty: "No further dates have been published for this school year.",
    today: "Today",
    tomorrow: "Tomorrow",
    inDays: (n: number) => `in ${n} ${n === 1 ? "day" : "days"}`,
    showPast: "Show completed term",
    hidePast: "Hide completed term",
    done: "Completed",
  },
  es: {
    upcoming: "Próximas fechas",
    empty: "No hay más fechas publicadas para este curso escolar.",
    today: "Hoy",
    tomorrow: "Mañana",
    inDays: (n: number) => `en ${n} ${n === 1 ? "día" : "días"}`,
    showPast: "Mostrar periodo finalizado",
    hidePast: "Ocultar periodo finalizado",
    done: "Finalizado",
  },
} as const;

/** Plockar första ISO-datumet ur en radetikett, t.ex. "2026-09-02 Onsdag". */
function startIso(label: string): string {
  return label.slice(0, 10);
}

/** Sista datumet i etiketten – intervall som "2026-10-26 – 2026-10-30" slutar senare. */
function endIso(label: string): string {
  const all = label.match(/\d{4}-\d{2}-\d{2}/g);
  return all && all.length > 1 ? all[all.length - 1]! : startIso(label);
}

function toDate(iso: string): number {
  const [y, m, d] = iso.split("-").map(Number);
  return Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

function todayUtc(): number {
  const now = new Date();
  return Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
}

function daysBetween(from: number, to: number): number {
  return Math.round((to - from) / 86_400_000);
}

type Entry = { term: SchoolTerm; label: string; text: string; start: number; end: number };

function allEntries(terms: SchoolTerm[]): Entry[] {
  return terms.flatMap((term) =>
    term.rows.map(([label, text]) => ({
      term,
      label,
      text,
      start: toDate(startIso(label)),
      end: toDate(endIso(label)),
    })),
  );
}

export function SchoolYearDates({
  locale = "sv",
  terms = [],
}: {
  locale?: Locale;
  terms?: SchoolTerm[];
}) {
  const t = T[locale];
  // Datumet läses först efter hydrering så att serverrenderad HTML aldrig krockar.
  const [today, setToday] = useState<number | null>(null);
  const [showPast, setShowPast] = useState(false);
  useEffect(() => setToday(todayUtc()), []);

  const entries = allEntries(terms);
  const upcoming = today === null ? [] : entries.filter((e) => e.end >= today).slice(0, 3);
  const nextKey = upcoming[0] ? `${upcoming[0].label}-${upcoming[0].text}` : null;

  function countdown(e: Entry): string {
    if (today === null) return "";
    const diff = daysBetween(today, e.start);
    if (diff <= 0 && e.end >= today) return t.today;
    if (diff === 1) return t.tomorrow;
    return t.inDays(diff);
  }

  const pastTerms = today === null ? [] : terms.filter((term) => toDate(term.lastDay) < today);
  const currentTerms = terms.filter((term) => !pastTerms.includes(term));

  function renderTerm(term: SchoolTerm, dimmed: boolean) {
    return (
      <section key={term.term}>
        <div className="flex flex-wrap items-baseline gap-3">
          <h2 className="text-2xl font-semibold">{term.term}</h2>
          {dimmed ? (
            <span className="rounded-full border border-border bg-sand px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground">
              {t.done}
            </span>
          ) : null}
        </div>
        <dl className="mt-5 divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
          {term.rows.map(([date, text], i) => {
            const key = `${date}-${text}`;
            const isNext = key === nextKey;
            const past = today !== null && toDate(endIso(date)) < today;
            return (
              <div
                key={`${date}-${i}`}
                className={[
                  "grid gap-1 px-5 py-4 sm:grid-cols-[13rem_1fr]",
                  isNext ? "border-l-[3px] border-l-primary bg-primary/5" : "",
                  past ? "opacity-55" : "",
                ].join(" ")}
              >
                <dt className="flex items-center gap-2 text-sm font-medium text-primary">{date}</dt>
                <dd className="text-sm leading-relaxed text-foreground/90">{text}</dd>
              </div>
            );
          })}
        </dl>
      </section>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-12 px-5 py-16">
      <section className="rounded-xl border border-primary/25 bg-primary/5 p-5 sm:p-6">
        <h2 className="flex items-center gap-2 font-display text-lg font-semibold">
          <CalendarDays className="size-5 text-primary" />
          {t.upcoming}
        </h2>
        {today === null ? (
          <div className="mt-4 h-24 animate-pulse rounded-lg bg-card/70" aria-hidden />
        ) : upcoming.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">{t.empty}</p>
        ) : (
          <ol className="mt-4 space-y-3">
            {upcoming.map((e) => (
              <li
                key={`${e.label}-${e.text}`}
                className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 rounded-lg border border-border bg-card px-4 py-3"
              >
                <span className="text-sm font-medium text-primary">{e.label}</span>
                <span className="order-3 w-full text-sm text-foreground/90 sm:order-2 sm:w-auto sm:flex-1">
                  {e.text}
                </span>
                <span className="order-2 text-xs font-medium text-muted-foreground sm:order-3">
                  {countdown(e)}
                </span>
              </li>
            ))}
          </ol>
        )}
      </section>

      {terms.length === 0 ? (
        <p className="rounded-xl border border-border bg-card p-5 text-sm text-muted-foreground">
          {t.empty}
        </p>
      ) : null}

      {currentTerms.map((term) => renderTerm(term, false))}

      {pastTerms.length > 0 ? (
        <div>
          <button
            type="button"
            onClick={() => setShowPast((v) => !v)}
            className="cursor-pointer rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground/80 transition-colors hover:border-primary/40 hover:text-primary"
          >
            {showPast ? t.hidePast : t.showPast}
          </button>
          {showPast ? (
            <div className="mt-8 space-y-12">{pastTerms.map((term) => renderTerm(term, true))}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
